import { useRef, useState, useEffect } from "react";

/**
 * A premium 6-digit one-time-code input.
 *
 * Features:
 *  - one box per digit, auto-advances as you type
 *  - backspace moves to and clears the previous box
 *  - arrow-key navigation between boxes
 *  - paste a full code into any box and it spreads across all of them
 *  - mobile keyboards show digits only (inputMode="numeric") and the OS
 *    one-time-code autofill is enabled (autoComplete="one-time-code")
 *  - a little "pop" animation each time a digit lands, plus an error shake
 *
 * @param {number}   length    number of digits (default 6)
 * @param {string}   value     current code as a string
 * @param {Function} onChange  called with the new code string
 * @param {Function} onComplete called once with the code when all digits filled
 * @param {boolean}  error     when true, shows the error styling + shake
 * @param {boolean}  disabled  disables all inputs (e.g. while verifying)
 * @param {boolean}  success   when true, shows the success (green) styling
 */
const OtpInput = ({
    length = 6,
    value = "",
    onChange,
    onComplete,
    error = false,
    disabled = false,
    success = false,
}) => {
    const inputsRef = useRef([]);
    const [popIndex, setPopIndex] = useState(-1);
    const [shake, setShake] = useState(false);

    const digits = value.split("").slice(0, length);
    while (digits.length < length) digits.push("");

    // Trigger the shake animation whenever the parent flags an error
    useEffect(() => {
        if (error) {
            setShake(true);
            const id = setTimeout(() => setShake(false), 400);
            // refocus the first box so the user can retype immediately
            inputsRef.current[0]?.focus();
            return () => clearTimeout(id);
        }
    }, [error]);

    const focusBox = (index) => {
        const el = inputsRef.current[index];
        if (el) {
            el.focus();
            el.select?.();
        }
    };

    const emit = (chars) => {
        const code = chars.join("").slice(0, length);
        onChange?.(code);
        if (code.length === length && !chars.includes("")) {
            onComplete?.(code);
        }
    };

    const handleChange = (index, raw) => {
        const onlyDigits = raw.replace(/\D/g, "");
        if (!onlyDigits) return;

        const next = [...digits];

        if (onlyDigits.length > 1) {
            // user typed/pasted multiple digits into one box – spread them out
            let cursor = index;
            for (const ch of onlyDigits) {
                if (cursor >= length) break;
                next[cursor] = ch;
                cursor += 1;
            }
            emit(next);
            setPopIndex(Math.min(cursor, length) - 1);
            focusBox(Math.min(cursor, length - 1));
            return;
        }

        next[index] = onlyDigits;
        emit(next);
        setPopIndex(index);
        if (index < length - 1) focusBox(index + 1);
    };

    const handleKeyDown = (index, e) => {
        if (e.key === "Backspace") {
            e.preventDefault();
            const next = [...digits];
            if (next[index]) {
                next[index] = "";
                emit(next);
            } else if (index > 0) {
                next[index - 1] = "";
                emit(next);
                focusBox(index - 1);
            }
        } else if (e.key === "ArrowLeft" && index > 0) {
            e.preventDefault();
            focusBox(index - 1);
        } else if (e.key === "ArrowRight" && index < length - 1) {
            e.preventDefault();
            focusBox(index + 1);
        }
    };

    const handlePaste = (e) => {
        e.preventDefault();
        const pasted = (e.clipboardData.getData("text") || "").replace(/\D/g, "");
        if (!pasted) return;
        const next = Array(length).fill("");
        for (let i = 0; i < length && i < pasted.length; i += 1) next[i] = pasted[i];
        emit(next);
        const lastFilled = Math.min(pasted.length, length) - 1;
        setPopIndex(lastFilled);
        focusBox(Math.min(pasted.length, length - 1));
    };

    // clear the one-shot pop highlight after it plays
    useEffect(() => {
        if (popIndex < 0) return;
        const id = setTimeout(() => setPopIndex(-1), 200);
        return () => clearTimeout(id);
    }, [popIndex]);

    return (
        <div
            className={`flex justify-center gap-2 sm:gap-3 ${shake ? "otp-shake" : ""}`}
            onPaste={handlePaste}
        >
            {digits.map((digit, index) => {
                const isFilled = digit !== "";
                const borderColor = error
                    ? "border-error"
                    : success
                    ? "border-success"
                    : isFilled
                    ? "border-primary"
                    : "border-gray-300";
                const ring = success
                    ? "ring-2 ring-success/30"
                    : isFilled && !error
                    ? "ring-2 ring-primary/20"
                    : "";
                return (
                    <input
                        key={index}
                        ref={(el) => (inputsRef.current[index] = el)}
                        type="text"
                        inputMode="numeric"
                        autoComplete={index === 0 ? "one-time-code" : "off"}
                        pattern="\d*"
                        maxLength={length}
                        value={digit}
                        disabled={disabled}
                        aria-label={`Digit ${index + 1}`}
                        onChange={(e) => handleChange(index, e.target.value)}
                        onKeyDown={(e) => handleKeyDown(index, e)}
                        onFocus={(e) => e.target.select()}
                        className={`h-14 w-11 sm:h-16 sm:w-14 text-center text-2xl sm:text-3xl font-semibold
                            rounded-xl border-2 bg-white text-gray-900 caret-primary
                            outline-none transition-all duration-150
                            disabled:opacity-60 disabled:cursor-not-allowed
                            ${borderColor} ${ring}
                            ${popIndex === index ? "otp-pop" : ""}`}
                    />
                );
            })}
        </div>
    );
};

export default OtpInput;
